import { CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LanguageSelector } from "@/components/language-selector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useInitialLanguage } from "@/hooks/use-initial-language";
import { useForgotPasswordForm } from "./use-forgot-password-form";

export function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tForms } = useTranslation("forms");
  const [emailSent, setEmailSent] = useState(false);

  // Handle initial language from URL query parameter
  useInitialLanguage();
  const { form, isLoading, onSubmit } = useForgotPasswordForm({
    onSuccess: () => setEmailSent(true),
  });

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        {/* Language Selector - Desktop (top-right) */}
        <div className="absolute top-4 right-4 hidden sm:block">
          <LanguageSelector />
        </div>
        <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <CardTitle className="text-2xl text-center">
                {t("forgotPassword.checkEmail")}
              </CardTitle>
              <CardDescription className="text-center">
                {t("forgotPassword.checkEmailMessage")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  {t("forgotPassword.backToLogin")}
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Language Selector - Mobile (under card) */}
          <div className="flex justify-center sm:hidden">
            <LanguageSelector />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Selector - Desktop (top-right) */}
      <div className="absolute top-4 right-4 hidden sm:block">
        <LanguageSelector />
      </div>
      <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">96Beauty</h1>
          <p className="text-muted-foreground">{t("forgotPassword.subtitle")}</p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">{t("forgotPassword.title")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Form {...form}>
              <form onSubmit={onSubmit} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("forgotPassword.email")}</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder={tForms("placeholders.email")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isLoading ? t("forgotPassword.sending") : t("forgotPassword.sendResetLink")}
                </Button>
              </form>
            </Form>

            <div className="text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                {t("forgotPassword.backToLogin")}
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Language Selector - Mobile (under card) */}
        <div className="flex justify-center sm:hidden">
          <LanguageSelector />
        </div>
      </div>
    </div>
  );
}
